import type { Theme } from "@api/types";
import { createContext, useContext, useEffect, useState } from "react";

interface ThemeContextType {
    theme: Theme;
    resolvedTheme: "light" | "dark";
    setTheme: (theme: Theme) => void;
    toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

function getSystemTheme(): "light" | "dark" {
    if (typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches) {
        return "dark";
    }
    return "light";
}

function resolveTheme(theme: Theme): "light" | "dark" {
    if (theme === "system") {
        return getSystemTheme();
    }
    return theme;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
    // Initialize theme from localStorage or default to light
    const [theme, setThemeState] = useState<Theme>(() => {
        if (typeof window !== "undefined") {
            try {
                const savedTheme = localStorage.getItem("storydoodle-theme") as Theme;
                if (savedTheme && ["light", "dark", "system"].includes(savedTheme)) {
                    return savedTheme;
                }
            } catch {
                // localStorage not available or failed
            }
        }
        return "system";
    });

    const resolvedTheme = resolveTheme(theme);

    // Apply theme to DOM and save to localStorage
    useEffect(() => {
        const root = document.documentElement;
        root.classList.remove("light", "dark");
        root.classList.add(resolvedTheme);

        // Persist theme to localStorage for next page load
        try {
            localStorage.setItem("storydoodle-theme", theme);
        } catch {
            // localStorage not available
        }
    }, [resolvedTheme, theme]);

    // Listen for system theme changes when using system theme
    useEffect(() => {
        if (theme !== "system") return;

        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        const handleChange = () => {
            const root = document.documentElement;
            root.classList.remove("light", "dark");
            root.classList.add(getSystemTheme());
        };

        mediaQuery.addEventListener("change", handleChange);
        return () => mediaQuery.removeEventListener("change", handleChange);
    }, [theme]);

    const setTheme = (newTheme: Theme) => {
        setThemeState(newTheme);
    };

    const toggleTheme = () => {
        if (theme === "light") {
            setTheme("dark");
        } else {
            setTheme("light");
        }
    };

    return (
        <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error("useTheme must be used within a ThemeProvider");
    }
    return context;
}
